import {
    PlannerWithDetails,
    TodoWithDetails,
} from '@/common/types/databaseTypes';
import { TOTAL_BLOCKS } from '@/common/utils/timeBlockUtils';
import {
    DragEndEvent,
    DragMoveEvent,
    DragStartEvent,
    UniqueIdentifier,
} from '@dnd-kit/core';
import { create } from 'zustand/react';
import { usePlannerStore } from './plannerStore';
import { getStartOfDay, isSameDate } from '@tickitacka/shared';

// 드래그 아이템의 타입을 나타내는 리터럴 타입
export type DragItemType = 'todo' | 'planner';

// 드롭 대상을 나타내는 리터럴 타입
export type DropTargetType = 'calendar' | 'todoList' | 'inbox';

/**
 * 기본 드래그 아이템 인터페이스
 *
 * @param {string} itemType: 'todo' | 'planner': 드래그하는 아이템의 데이터 타입
 * @param {string} targetType: 'calendar' | 'todoList' | 'inbox': 어디에 드롭할 것인지
 */
export interface BaseDragData {
    itemType: DragItemType; // 드래그하는 객체가 무엇인지
    targetType: DropTargetType | null; // 어디에 드롭할 것인지
}

/**
 * 플래너 드래그 데이터 인터페이스
 *
 * @param {string} itemType: 'planner': 드래그하는 객체가 플래너임을 명시
 * @param {PlannerWithDetails} planner
 * @param {{ start: number; end: number } | null} range: 플래너 데이터의 기존 blocks range, 블럭 이동일 경우 Not null
 */
export interface PlannerDragData extends BaseDragData {
    itemType: 'planner';
    planner: PlannerWithDetails;
    range: { start: number; end: number } | null; // 블럭 이동일 경우 Not null
}

/**
 * 투두 드래그 데이터 인터페이스
 *
 * @param {string} itemType: 'todo': 드래그하는 객체가 투두임을 명시
 * @param {TodoWithDetails} todo
 */
export interface TodoDragData extends BaseDragData {
    itemType: 'todo';
    todo: TodoWithDetails;
}

type DragItemData = PlannerDragData | TodoDragData;

/**
 * 드랍 결과 타입에서 사용하는 action 타입들 - 어떤 로직을 수행할지를 결정함
 * `[출발지]_TO_[목적지]_[작업]` 형식
 *
 * 출발지, 목적지 키워드
 * - calendar: 플래너 캘린더 위치에서 blocks가 비어있지 않은 Planner
 * - unscheduled: 플래너 캘린더 상단 위치, blocks가 비어있는 Planner
 * - today: '오늘 계획한 할 일' 위치의 Planner
 * - unfinished: '끝내지 못한 할 일' 위치의 Planner 또는 Todo
 * - inbox: '인박스' 위치의 Todo
 *
 * 작업 키워드
 * - CREATE: 새 플래너 데이터를 생성
 * - MOVE: 데이터의 특정 필드 값을 업데이트 (플래너: blocks, 투두: goalId)
 */
export enum DragEndAction {
    /**
     * 플래너 캘린더 영역에서 blocks가 비어있지 않은 Planner를 unscheduled 영역으로 드롭 (blocks 비움)
     */
    CALENDAR_TO_UNSCHEDULED_MOVE = 'CALENDAR_TO_UNSCHEDULED_MOVE',

    /**
     * 플래너 캘린더 영역에서 blocks가 비어있지 않은 Planner를 다른 플래너 영역으로 드롭 (blocks 업데이트)
     */
    CALENDAR_TO_CALENDAR_MOVE = 'CALENDAR_TO_CALENDAR_MOVE',

    /**
     * 플래너 캘린더 내 unscheduled 영역에서 blocks가 비어있지 않은 Planner를 플래너로 드롭 (blocks 추가)
     */
    UNSCHEDULED_TO_CALENDAR_MOVE = 'UNSCHEDULED_TO_CALENDAR_MOVE',

    /**
     * '오늘 계획한 할 일'에서 Planner를 플래너 캘린더로 드롭 (blocks 추가)
     */
    TODAY_TO_CALENDAR_MOVE = 'TODAY_TO_CALENDAR_MOVE',

    /**
     * '끝내지 못한 할 일'에서 Planner를 플래너 캘린더 드롭, 새 Planner 생성
     */
    UNFINISHED_TO_CALENDAR_CREATE = 'UNFINISHED_TO_CALENDAR_CREATE',
    /**
     * '끝내지 못한 할 일'에서 Planner를 '오늘 계획한 할 일'으로 드롭, 새 Planner 생성 (빈 blocks)
     */
    UNFINISHED_TO_TODAY_CREATE = 'UNFINISHED_TO_TODAY_CREATE',

    /**
     * '인박스'에서 Planner를 플래너 캘린더 드롭, 새 Planner 생성
     */
    INBOX_TO_CALENDAR_CREATE = 'INBOX_TO_CALENDAR_CREATE',
    /**
     * '인박스'에서 Planner를 '오늘 계획한 할 일'으로 드롭, 새 Planner 생성 (빈 blocks)
     */
    INBOX_TO_TODAY_CREATE = 'INBOX_TO_TODAY_CREATE',
    /**
     * '인박스' 내에서의 Todo 드롭 (goal_id 업데이트)
     */
    INBOX_TO_INBOX_MOVE = 'INBOX_TO_INBOX_MOVE',
}

/**
 * 드랍 결과 타입에서 사용하는 action 타입들 - 어떤 로직을 수행할지를 결정함
 * [출발지]_TO_[목적지]_[작업] 형식
 */
export type DragEndActionType = DragEndAction;

/**
 * drag end result type
 */
interface BaseDragEndResult {
    action: DragEndActionType;
    targetType: DropTargetType | null;
}

/**
 * '끝내지 못한 할 일', '오늘 계획한 할 일'의 Planner -> 플래너 캘린더 드랍 결과
 *
 * @param {string} targetType: 드랍할 위치가 어디인지, 'calendar' (플래너 캘린더)
 * @param {DragEndAction} action: 드랍 결과 타입 (DragEndAction)
 * @param {string} plannerId: 드래그 중인 Planner ID
 * @param {PlannerWithDetails | null} planner: 드래그 중인 Planner
 * @param {{ start: number; end: number } | null} originalRange: blocks가 채워져있는 Planner를 이동하는 경우 originalRange 명시
 */
export interface CalendarDragEndResult extends BaseDragEndResult {
    targetType: 'calendar';
    action:
        | DragEndAction.CALENDAR_TO_UNSCHEDULED_MOVE
        | DragEndAction.CALENDAR_TO_CALENDAR_MOVE
        | DragEndAction.UNSCHEDULED_TO_CALENDAR_MOVE
        | DragEndAction.TODAY_TO_CALENDAR_MOVE
        | DragEndAction.UNFINISHED_TO_CALENDAR_CREATE;
    plannerId: string;
    planner: PlannerWithDetails | null;
    originalRange: { start: number; end: number } | null;
}

/**
 * '인박스'의 Todo -> 플래너 캘린더 드랍 결과 타입
 *
 * @param {string} targetType: 드랍할 위치가 어디인지, 'calendar' (플래너 캘린더)
 * @param {DragEndAction} action: 드랍 결과 타입 (DragEndAction)
 * @param {string} todoId: 드래그 중인 Todo ID
 * @param {string} userId: 투두의 주인 uid
 * @param {string | null} goalId: 투두의 목표 ID
 */
export interface TodoToCalendarDragEndResult extends BaseDragEndResult {
    targetType: 'calendar';
    action: DragEndAction.INBOX_TO_CALENDAR_CREATE;
    todoId: string;
    userId: string;
    goalId: string | null;
}

/**
 * '인박스'의 Todo, '끝내지 못한 할 일'의 Planner -> '오늘 계획한 할 일' 드랍 결과 타입
 *
 * @param {string} targetType: 드랍할 위치가 어디인지, 'todoList'
 * @param {DragEndAction} action: 드랍 결과 타입 (DragEndAction)
 * @param {string} todoId: 드래그 중인 Todo ID
 * @param {string} userId: 투두의 주인 uid
 * @param {string | null} goalId: 투두의 목표 ID
 */
export interface TodoListDragEndResult extends BaseDragEndResult {
    targetType: 'todoList';
    action:
        | DragEndAction.INBOX_TO_TODAY_CREATE
        | DragEndAction.UNFINISHED_TO_TODAY_CREATE;
    todoId: string;
    userId: string;
    goalId: string | null;
}

/**
 * '인박스' 내에서의 Todo 드랍 결과 타입
 *
 * @param {string} targetType: 드랍할 위치가 어디인지, 'inbox'
 * @param {DragEndAction} action: 드랍 결과 타입 (DragEndAction)
 * @param {string} todoId: 드래그 중인 Todo ID
 */
export interface InboxDragEndResult extends BaseDragEndResult {
    targetType: 'inbox';
    action: DragEndAction.INBOX_TO_INBOX_MOVE;
    todoId: string;
    goalId: string | null;
}

/**
 * 드랍 결과 타입
 */
export type DragEndResult =
    | CalendarDragEndResult
    | TodoListDragEndResult
    | TodoToCalendarDragEndResult
    | InboxDragEndResult;

// 드래그 중인 아이템의 데이터 타입 확인
/**
 *
 * @param data 현재 드래그 중인 데이터(activeDragItem?.data)
 * @returns {boolean} data가 TodoDragData 타입인지 확인 (드래그 중인 아이템이 투두인지 확인)
 */
export function isTodoDragData(
    data: DragItemData | null
): data is TodoDragData {
    return data?.itemType === 'todo';
}

/**
 *
 * @param data 현재 드래그 중인 데이터(activeDragItem?.data)
 * @returns {boolean} data가 PlannerDragData 타입인지 확인 (드래그 중인 아이템이 플래너인지 확인)
 */
export function isPlannerDragData(
    data: DragItemData | null
): data is PlannerDragData {
    return data?.itemType === 'planner';
}

// 대상 타입 확인

/**
 *
 * @param data 현재 드래그 중인 데이터(activeDragItem?.data)
 * @returns {boolean} data가 CalendarDragEndResult 타입인지 확인 (드랍하려는 영역이 플래너 캘린더인지 확인)
 */
export function isCalendarTarget(data: DragItemData | null): boolean {
    return data?.targetType === 'calendar';
}

/**
 *
 * @param data 현재 드래그 중인 데이터(activeDragItem?.data)
 * @returns {boolean} data가 TodoListDragEndResult 타입인지 확인 (드랍하려는 영역이 TodoList인지 확인)
 */
export function isTodoListTarget(data: DragItemData | null): boolean {
    return data?.targetType === 'todoList';
}

/**
 *
 * @param data 현재 드래그 중인 데이터(activeDragItem?.data)
 * @returns {boolean} data가 InboxDragEndResult 타입인지 확인 (드랍하려는 영역이 인박스인지 확인)
 */
export function isInboxTarget(data: DragItemData | null): boolean {
    return data?.targetType === 'inbox';
}

/**
 *
 * @param id 호버 중인 곳의 Droppable의 ID
 * @returns {boolean} 드랍하려는 위치가 TimeBlockContainer인지 확인
 */
function isTimeBlockContainer(id: UniqueIdentifier): boolean {
    const idStr = id.toString();

    return idStr.startsWith('time-');
}

/**
 *
 * @param id 호버 중인 곳의 Droppable의 ID
 * @returns {boolean} 드랍하려는 위치가 TodoListContainer인지 확인
 */
function isTodoListContainer(id: UniqueIdentifier): boolean {
    const idStr = id.toString();

    return idStr.startsWith('planner-');
}

/**
 *
 * @param id 호버 중인 곳의 Droppable의 ID
 * @returns {boolean} 드랍하려는 위치가 인박스인지 확인
 */
function isInboxContainer(id: UniqueIdentifier): boolean {
    const idStr = id.toString();

    return idStr.startsWith('goal-');
}

/**
 * 현재 드래그 중인 아이템
 * itemType: 'todo' | 'planner': 드래그하는 객체가 무엇인지
 * targetType: 'calendar' | 'todoList': 어디에 드랍할 것인지
 * data: PlannerDragData | TodoDragData: 드래그 중인 아이템 데이터
 */
interface ActiveDragItem {
    // id: string;
    data: DragItemData;
}

interface DragState {
    enable: boolean;
    // 현재 드래그 중인 아이템
    activeDragItem: ActiveDragItem | null;

    // 현재 드래그 아이템이 위치한 드롭존
    activeDropZone: string | null;

    // 플래너(캘린더)에 드랍할 때 사용하는 변수들
    // 점유된 시간 블록 맵
    occupiedBlocksMap: Map<number, string>;

    // 상태 설정 액션
    setEnable: (enable: boolean) => void;
    setDragState: (state: Partial<DragState>) => void;
    setActiveDragItem: (item: ActiveDragItem | null) => void;
    setActiveDropZone: (zone: string | null) => void;
    updateOccupiedBlocks: (blocks: Map<number, string>) => void;

    // 드래그 앤 드롭 핸들러
    handleDragStart: (event: DragStartEvent) => void;
    handleDragEnd: <T extends DragEndResult>(event: DragEndEvent) => T | null;

    handlePlannerDragStart: (event: DragStartEvent) => void;
    handleTodoDragStart: (event: DragStartEvent) => void;

    handleDragMove: (event: DragMoveEvent) => void;

    handleDragEndToInbox: <T extends DragEndResult>(
        event: DragEndEvent
    ) => T | null;
    handleDragEndToTodoList: <T extends DragEndResult>(
        event: DragEndEvent
    ) => T | null;
    handleDragEndToCalendar: <T extends DragEndResult>(
        event: DragEndEvent
    ) => T | null;

    // 리셋
    reset: () => void;
}
export const useDragStore = create<DragState>((set, get) => ({
    // 초기 상태
    // dragPhase: 'idle',
    // startBlock: 0,
    // endBlock: 0,
    // resizingPlannerId: null,
    // isStartHandle: false,
    enable: true,

    activeDragItem: null,
    activePlanner: null,
    activeRange: null,
    activeDropZone: null,
    occupiedBlocksMap: new Map(),

    // 상태 설정 액션
    // setDragPhase: phase => set({ dragPhase: phase }),

    setDragState: state => set(state),

    setEnable: state => set({ enable: state }),

    setActiveDragItem: item => set({ activeDragItem: item }),

    setActiveDropZone: zone => set({ activeDropZone: zone }),

    updateOccupiedBlocks: blocks => set({ occupiedBlocksMap: blocks }),

    // 드래그 시작 핸들러
    handleDragStart: event => {
        const state = get();
        if (!state.enable) return;

        const { active } = event;
        if (!active) return;

        const data = active.data.current as DragItemData;
        if (!data) return;

        if (isTodoDragData(data)) {
            state.handleTodoDragStart(event);
        } else if (isPlannerDragData(data)) {
            state.handlePlannerDragStart(event);
        }
    },

    handleTodoDragStart: event => {
        const state = get();
        if (!state.enable) return;

        const { active } = event;
        if (!active) return;

        const data = active.data.current as TodoDragData;
        if (!data) return;

        const todoData = data.todo;
        set({
            activeDragItem: {
                data: {
                    itemType: 'todo',
                    todo: todoData,
                    targetType: 'todoList',
                },
            },
        });
    },

    handlePlannerDragStart: event => {
        const state = get();
        if (!state.enable) return;

        const { active } = event;

        const planner = (active.data.current as PlannerDragData)?.planner;

        if (planner) {
            // 드래그 타입 확인
            const isScheduled = active.data.current?.range !== null;
            // 스케줄된 플래너를 이동하는 것일 경우, 기존의 range 저장
            if (isScheduled) {
                const range = (active.data.current as PlannerDragData).range;
                set({
                    activeDragItem: {
                        data: {
                            itemType: 'planner',
                            targetType: 'calendar',
                            planner,
                            range,
                        },
                    },
                });
            } else {
                set({
                    activeDragItem: {
                        data: {
                            itemType: 'planner',
                            targetType: 'calendar',
                            planner,
                            range: null,
                        },
                    },
                });
            }
        }
    },

    // 드래그 이동 핸들러
    handleDragMove: event => {
        const state = get();
        if (!state.activeDragItem) return;

        const { over } = event;
        if (!over) return; // 갖고있던 targetType 초기화 방지

        // 현재 위치한 영역에 따라 targetType 결정
        let newTargetType: DropTargetType | null = null;

        // Calendar 영역인지 확인
        if (over && isTimeBlockContainer(over.id)) {
            newTargetType = 'calendar';
        }
        // TodoList 영역인지 확인
        else if (over && isTodoListContainer(over.id)) {
            newTargetType = 'todoList';
        }
        // Inbox 영역인지 확인
        else if (over && isInboxContainer(over.id)) {
            newTargetType = 'inbox';
        } else {
            newTargetType = null;
        }

        // targetType이 변경되었으면 상태 업데이트
        if (newTargetType !== state.activeDragItem?.data.targetType) {
            set({
                activeDragItem: {
                    ...state.activeDragItem,
                    data: {
                        ...state.activeDragItem.data,
                        targetType: newTargetType,
                    },
                },
            });
        }
    },

    // 드래그 종료 핸들러
    handleDragEnd: <T extends DragEndResult>(event: DragEndEvent): T | null => {
        const state = get();
        if (!state.activeDragItem) return null;

        const data = state.activeDragItem.data;
        // const data = event.active.data.current as DragItemData;

        // 무엇을 드래그 중인지 확인
        if (isTodoDragData(data)) {
            // Todo 아이템 처리
            const todoData = data.todo;

            // 어디에 드롭할지 확인
            if (isCalendarTarget(data)) {
                // Calendar에 Todo 배치 로직
                return state.handleDragEndToCalendar(event);
            } else if (isTodoListTarget(data)) {
                // TodoList에 Todo 배치 로직
                return state.handleDragEndToTodoList(event);
            } else if (isInboxTarget(data)) {
                // 인박스에 Todo 배치 로직
                return state.handleDragEndToInbox(event);
            }
        } else {
            // Planner 아이템 처리
            const plannerData = data.planner;

            if (isCalendarTarget(data)) {
                // Calendar에 Planner 배치 로직
                return state.handleDragEndToCalendar(event);
            } else if (isTodoListTarget(data)) {
                // TodoList에 Planner 배치 로직
                return state.handleDragEndToTodoList(event);
            }
        }

        return null;
    },

    handleDragEndToInbox: <T extends DragEndResult>(
        event: DragEndEvent
    ): T | null => {
        const state = get();
        const { active, over } = event;

        if (!active || !over || !state.activeDragItem) {
            set({ activeDragItem: null });
            return null;
        }

        const isTodoDrag = isTodoDragData(state.activeDragItem?.data);

        // 인박스 -> 인박스, 투두 데이터 드래그 처리
        if (isTodoDrag) {
            const dragData = state.activeDragItem.data as TodoDragData;

            set({ activeDragItem: null });

            // goal_id 업데이트 로직
            return {
                targetType: 'inbox',
                action: DragEndAction.INBOX_TO_INBOX_MOVE,
                todoId: dragData.todo.id,
                goalId: dragData.todo.goal_id,
            } as T;
        }

        return null;
    },

    handleDragEndToTodoList: <T extends DragEndResult>(
        event: DragEndEvent
    ): T | null => {
        const state = get();
        const { active, over } = event;

        if (!active || !over || !state.activeDragItem) {
            set({ activeDragItem: null });
            return null;
        }

        const isTodoDrag = isTodoDragData(state.activeDragItem?.data);

        // 투두 데이터(인박스에서) -> '오늘 계획한 할 일' 케이스 처리
        if (isTodoDrag) {
            const dragData = state.activeDragItem.data as TodoDragData;

            // planner 데이터 추가 로직
            set({ activeDragItem: null });

            return {
                targetType: 'todoList',
                action: DragEndAction.INBOX_TO_TODAY_CREATE,
                todoId: dragData.todo.id,
                goalId: dragData.todo.goal_id,
                userId: dragData.todo.user_id,
            } as T;
        }

        // 플래너 데이터('끝내지 못한 할 일'에서) -> '오늘 계획한 할 일' 케이스 처리
        const dragData = state.activeDragItem.data as PlannerDragData;

        set({ activeDragItem: null });

        return {
            targetType: 'todoList',
            action: DragEndAction.UNFINISHED_TO_TODAY_CREATE,
            todoId: dragData.planner.todo_id,
            goalId: null,
            userId: dragData.planner.user_id,
        } as T;
    },

    handleDragEndToCalendar: <T extends DragEndResult>(
        event: DragEndEvent
    ): T | null => {
        const state = get();
        const { active, over } = event;

        if (!active || !over || !state.activeDragItem) {
            set({ activeDragItem: null });
            return null;
        }

        const isTodoDrag = isTodoDragData(state.activeDragItem?.data);

        // 투두 데이터(인박스에서) -> 플래너 캘린더 케이스 처리
        if (isTodoDrag) {
            const dragData = state.activeDragItem.data as TodoDragData;

            set({ activeDragItem: null });

            return {
                targetType: 'calendar',
                action: DragEndAction.INBOX_TO_CALENDAR_CREATE,
                todoId: dragData.todo.id,
                goalId: dragData.todo.goal_id,
                userId: dragData.todo.user_id,
            } as T;
        }

        // 플래너 데이터 -> 플래너 캘린더 케이스들 처리
        const dragData = state.activeDragItem.data as PlannerDragData;

        // 드래그 중인 planner_date와 calendarDate 비교
        const planner = (active?.data?.current as PlannerDragData)?.planner;
        const plannerDate = planner?.planner_date;
        const calendarDate = usePlannerStore.getState().calendarDate;

        if (plannerDate) {
            const needAdd = !isSameDate(
                plannerDate,
                getStartOfDay(calendarDate)
            );
            if (needAdd) {
                // planner 데이터 추가 로직
                set({ activeDragItem: null });

                return {
                    targetType: 'calendar',
                    action: DragEndAction.UNFINISHED_TO_CALENDAR_CREATE,
                    plannerId: planner.id,
                    planner: planner,
                    originalRange: null,
                } as T;
            }
        }

        // unscheduled 영역에 드롭한 경우 처리
        if (over.id.toString().includes('--1')) {
            set({
                activeDragItem: null,
            });

            return {
                targetType: 'calendar',
                action: DragEndAction.CALENDAR_TO_UNSCHEDULED_MOVE,
                plannerId: dragData.planner.id,
                planner: dragData.planner,
                originalRange: dragData.range,
            } as T;
        }

        // 드롭 위치가 시간 블록 영역인지 확인
        if (over.id.toString().startsWith('time-')) {
            // 시간 블록 ID에서 시간 인덱스 추출 (예: "time-15" → 15)
            const timeIndex = parseInt(
                over.id.toString().replace('time-', ''),
                10
            );

            // 유효한 시간 인덱스인지 확인
            if (
                !isNaN(timeIndex) &&
                timeIndex >= 0 &&
                timeIndex < TOTAL_BLOCKS
            ) {
                // range가 있으면 range length 유지해 업데이트, 없으면 드랍한 위치 기준으로 block update
                const maintainRangeLength =
                    active.data.current?.range !== null &&
                    active.data.current?.range !== undefined;

                // 결과 객체 초기화
                const result = {
                    targetType: 'calendar',
                    action: '',
                    plannerId: dragData.planner.id,
                    planner: dragData.planner,
                    originalRange: null,
                };

                if (maintainRangeLength) {
                    // 스케줄된 플래너 처리 로직
                    const originalRange = dragData.range;

                    if (originalRange) {
                        result.action = DragEndAction.CALENDAR_TO_CALENDAR_MOVE;
                        result.originalRange = originalRange;
                        // 여기에 블록 계산 로직 추가 (컴포넌트에서 제공)
                    }
                } else {
                    if (dragData.planner?.blocks?.length > 0) {
                        // 이미 블록이 있는 플래너(오늘 계획한 할일)를 다른 시간대에 추가 배치
                        result.action = DragEndAction.TODAY_TO_CALENDAR_MOVE;
                    } else {
                        // unscheduled 플래너 처리 로직
                        result.action =
                            DragEndAction.UNSCHEDULED_TO_CALENDAR_MOVE;
                        // 여기에 블록 계산 로직 추가 (컴포넌트에서 제공)
                    }
                }
                // 상태 리셋
                set({
                    activeDragItem: null,
                });

                return result as T;
            }
        }

        // 기본 리셋
        set({
            activeDragItem: null,
        });

        return null;
    },

    // 리셋
    reset: () =>
        set({
            // dragPhase: 'idle',
            // startBlock: 0,
            // endBlock: 0,
            // resizingPlannerId: null,
            // isStartHandle: false,
            activeDragItem: null,
            activeDropZone: null,
            occupiedBlocksMap: new Map(),
        }),
}));
